import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Chip,
  Skeleton,
  Stack,
  Button,
  Paper,
  Divider,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack,
  OpenInNew,
  PhotoLibrary,
  CalendarMonth,
  Inventory2,
  MenuBook,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  marketingBandCtaSx,
  marketingColor,
  marketingPhotoPlateSx,
  marketingRaisedBandSx,
  marketingSurface,
} from '../theme/marketingTokens';
import axios from 'axios';
import { apiService, Product } from '../services/api';
import { ImageGallery } from '../components/ImageGallery';
import { SpecificationsViewer } from '../components/SpecificationsViewer';
import { stripHtml } from '../utils/text';
import { parseFirestoreTimestamp } from '../utils/firestoreDate';
import { getEffectiveMap } from '../utils/mapPricing';
import { manufacturerSiteUrl } from '../utils/manufacturerSiteUrl';

type DemoLocationState = {
  product?: Product;
};

function normalizeDemoId(raw?: string): string {
  if (!raw) return '';
  let decoded = raw.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // Keep the raw value when it is not URI-encoded.
  }
  return decoded;
}

function formatMoney(value?: number | null): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatDateLabel(value?: unknown): string | null {
  if (value == null || value === '') return null;
  const d = parseFirestoreTimestamp(value);
  if (d) {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return typeof value === 'string' ? value : null;
}

function priceAsOfValue(product: Product): unknown {
  return product.pricing?.vendorPriceAsOf ?? product.pricing?.priceAsOf ?? null;
}

function docTypeBadge(type?: string): string {
  const t = (type || 'doc').toLowerCase();
  if (t === 'manual') return 'PDF';
  if (t === 'firmware') return 'FW';
  if (t === 'spec-sheet' || t === 'spec') return 'SPEC';
  if (t === 'driver') return 'DRV';
  if (t === 'support-page') return 'HELP';
  return 'DOC';
}

function productImage(product: Product): string {
  return product.images?.find((url) => typeof url === 'string' && url.trim()) || '/placeholder-camera.svg';
}

function isPlaceholderSpecValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  return (
    !v ||
    v === 'n/a' ||
    v === '-' ||
    v.includes('click here') ||
    v.includes('see full') ||
    v.includes('full specifications')
  );
}

/** Prefer structured catalog fields; fall back to useful specification text. */
export function findSpecValue(specs: Record<string, string> | undefined, needles: string[]): string | null {
  if (!specs) return null;
  const entries = Object.entries(specs);
  for (const needle of needles) {
    // Dotted path needles (e.g. physical.weight) must not substring-match siblings
    // like physical.weightWithBattery.
    const allowSubstring = !needle.includes('.');
    for (const [key, raw] of entries) {
      const low = key.toLowerCase();
      if (!(low === needle || low.endsWith(`.${needle}`) || (allowSubstring && low.includes(needle)))) {
        continue;
      }
      const cleaned = stripHtml(raw).trim();
      if (cleaned && !isPlaceholderSpecValue(cleaned)) {
        return cleaned;
      }
    }
  }
  return null;
}

function formatWeight(product: Product): string | null {
  const dims = product.dimensions;
  if (dims?.weight != null && !Number.isNaN(Number(dims.weight))) {
    return `${dims.weight} ${dims.weightUnit || 'g'}`.trim();
  }
  return (
    findSpecValue(product.specifications, ['physical.weight', 'weight with battery', 'weight']) ||
    null
  );
}

function formatDimensions(product: Product): string | null {
  const dims = product.dimensions;
  if (dims?.length != null && dims?.width != null && dims?.height != null) {
    return `${dims.length} × ${dims.width} × ${dims.height} ${dims.unit || 'mm'}`.trim();
  }
  return (
    findSpecValue(product.specifications, [
      'physical.dimensions',
      'dimensions (wxhxd)',
      'dimensions',
    ]) || null
  );
}

function DetailRows({ rows }: { rows: Array<{ label: string; value: string }> }) {
  if (rows.length === 0) return null;
  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {row.label}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
            {row.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function ProductIdentifiersPanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const manufacturer = product.vendorName || product.brand;
  const ean =
    product.ean && product.ean !== product.upc
      ? product.ean
      : null;
  const rows = [
    { label: t('public.home.demos.detail.manufacturer'), value: manufacturer },
    { label: t('public.home.demos.detail.modelNumber'), value: product.model },
    { label: t('public.home.demos.detail.sku'), value: product.sku },
    { label: t('public.home.demos.detail.upc'), value: product.upc },
    { label: t('public.home.demos.detail.ean'), value: ean },
    { label: t('public.home.demos.detail.mpn'), value: product.mpn },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  if (rows.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('public.home.demos.detail.productDetails')}
      </Typography>
      <DetailRows rows={rows} />
    </Paper>
  );
}

function WeightDimensionsPanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const rows = [
    { label: t('public.home.demos.detail.weight'), value: formatWeight(product) },
    { label: t('public.home.demos.detail.dimensions'), value: formatDimensions(product) },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  if (rows.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('public.home.demos.detail.weightDimensions')}
      </Typography>
      <DetailRows rows={rows} />
    </Paper>
  );
}

function ProductTimelinePanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const ld = product.launchDates || {};
  const rows: Array<{ label: string; value: string; note?: string }> = [];

  const announced = formatDateLabel(ld.announced);
  if (announced) {
    rows.push({
      label: t('public.home.demos.detail.announced'),
      value: announced,
      note: typeof ld.announcedNote === 'string' ? ld.announcedNote : undefined,
    });
  }

  const usDate = formatDateLabel(ld.us?.date || product.launchDate);
  if (usDate) {
    rows.push({
      label: t('public.home.demos.detail.launchUs'),
      value: usDate,
      note: typeof ld.us?.note === 'string' ? ld.us.note : undefined,
    });
  }

  const canadaDate = formatDateLabel(ld.canada?.date);
  if (canadaDate) {
    rows.push({
      label: t('public.home.demos.detail.launchCanada'),
      value: canadaDate,
      note: typeof ld.canada?.note === 'string' ? ld.canada.note : undefined,
    });
  }

  const globalDate = formatDateLabel(ld.global?.date);
  if (globalDate) {
    rows.push({
      label: t('public.home.demos.detail.launchGlobal'),
      value: globalDate,
      note: typeof ld.global?.note === 'string' ? ld.global.note : undefined,
    });
  }

  if (rows.length === 0 && product.launchDate) {
    const launch = formatDateLabel(product.launchDate);
    if (launch) {
      rows.push({ label: t('public.home.demos.detail.launchDate'), value: launch });
    }
  }

  const stock = product.manufacturerStock;
  const hasStock = !!stock?.status && stock.status !== 'unknown';

  if (rows.length === 0 && !hasStock) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <CalendarMonth color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          {t('public.home.demos.detail.timeline')}
        </Typography>
      </Stack>
      <Stack spacing={1.25}>
        {rows.map((row) => (
          <Box key={row.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.value}
              </Typography>
            </Box>
            {row.note ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {row.note}
              </Typography>
            ) : null}
          </Box>
        ))}
        {hasStock && stock ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('public.home.demos.detail.manufacturerStock')}
            </Typography>
            <Chip
              size="small"
              label={stock.label || String(stock.status || '').replace(/_/g, ' ')}
              color={
                stock.status === 'in_stock'
                  ? 'success'
                  : stock.status === 'out_of_stock'
                    ? 'error'
                    : 'default'
              }
              variant="outlined"
            />
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function FeaturesPanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const features = (product.features || []).map((f) => stripHtml(f)).filter(Boolean);
  const highlights = (product.highlights || []).map((h) => stripHtml(h)).filter(Boolean);
  // Prefer dedicated features; fall back to highlights when features are absent.
  const items = features.length > 0 ? features : highlights;
  if (items.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('public.home.demos.detail.features')}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.slice(0, 12).map((item) => (
          <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5, lineHeight: 1.5 }}>
            {item}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
}

function InTheBoxPanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const items = product.inTheBox || [];
  if (items.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Inventory2 color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          {t('public.home.demos.detail.inTheBox')}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1.5,
        }}
      >
        {items.map((item) => (
          <Paper
            key={`${item.name}-${item.sku || ''}`}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: marketingSurface.accentTint,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" color="primary" fontWeight={700}>
                {item.quantity && item.quantity > 1 ? `×${item.quantity}` : '✓'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500}>
                {item.name}
              </Typography>
              {item.sku ? (
                <Typography variant="caption" color="text.secondary">
                  {t('public.home.demos.detail.sku')}: {item.sku}
                </Typography>
              ) : null}
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}

function ResourcesPanel({ product }: { product: Product }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const videos = product.videos || [];
  const docs = product.documentation || [];
  if (videos.length === 0 && docs.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <MenuBook color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          {t('public.home.demos.detail.resources')}
        </Typography>
      </Stack>

      {videos.length > 0 ? (
        <Box sx={{ mb: docs.length > 0 ? 2.5 : 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t('public.home.demos.detail.videos')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.5,
            }}
          >
            {videos.map((video) => (
              <Paper
                key={video.url}
                variant="outlined"
                component="a"
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                {video.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={video.thumbnailUrl}
                    alt={video.title || t('public.home.demos.detail.videos')}
                    sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <Box sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {video.title || t('public.home.demos.detail.watchVideo')}
                  </Typography>
                  {video.type ? (
                    <Chip
                      label={video.type}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, fontSize: '0.7rem', textTransform: 'capitalize' }}
                    />
                  ) : null}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ) : null}

      {docs.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t('public.home.demos.detail.documentation')}
          </Typography>
          <Stack spacing={1}>
            {docs.map((doc) => (
              <Paper
                key={`${doc.url}-${doc.title}`}
                variant="outlined"
                component="a"
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {docTypeBadge(doc.type)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {doc.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {(doc.type || 'document').replace(/-/g, ' ')}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="primary" fontWeight={600} sx={{ flexShrink: 0 }}>
                  {t('public.home.demos.detail.openResource')} →
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Paper>
  );
}

function ProductListView({
  products,
  loading,
  error,
}: {
  products: Product[];
  loading: boolean;
  error: string | null;
}) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const navigate = useNavigate();

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.75rem' } }}
          >
            {t('public.home.demos.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 720, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}
          >
            {t('public.home.demos.subtitle')}
          </Typography>
        </Box>

        {error ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
          }}
        >
          {loading
            ? [0, 1, 2].map((i) => (
                <Card key={i} sx={{ borderRadius: 3 }}>
                  <Skeleton variant="rectangular" height={220} />
                  <CardContent>
                    <Skeleton width="40%" />
                    <Skeleton width="80%" />
                    <Skeleton width="30%" />
                  </CardContent>
                </Card>
              ))
            : products.map((product) => {
                const description = stripHtml(
                  product.shortDescription || product.description || ''
                );
                return (
                  <Card
                    key={product.id}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() =>
                        navigate(`/product-demos/${encodeURIComponent(product.id)}`, {
                          state: { product } satisfies DemoLocationState,
                        })
                      }
                      sx={{ height: '100%', alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}
                    >
                      <CardMedia
                        component="img"
                        height="220"
                        image={productImage(product)}
                        alt={product.name || product.model}
                        sx={{ objectFit: 'contain', ...marketingPhotoPlateSx, p: 2 }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.src = '/placeholder-camera.svg';
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <Chip label={product.brand} size="small" color="primary" variant="outlined" />
                          {product.category ? (
                            <Chip label={product.category} size="small" variant="outlined" />
                          ) : null}
                        </Stack>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          {product.name || product.model}
                        </Typography>
                        {(product.priceFormatted || product.price) ? (
                          <Typography variant="subtitle1" color="primary.main" fontWeight={600} sx={{ mb: 1 }}>
                            {product.priceFormatted || formatMoney(product.price)}
                          </Typography>
                        ) : null}
                        {description ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {description}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="primary" sx={{ mt: 2, fontWeight: 600 }}>
                          {t('public.home.demos.viewDetails')} →
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
        </Box>

        {!loading && products.length === 0 && !error ? (
          <Alert severity="info">{t('public.home.demos.empty')}</Alert>
        ) : null}
      </Container>
    </Box>
  );
}

function ProductDetailView({ id }: { id: string }) {
  const { t } = useTranslation() as { t: (key: string) => string };
  const navigate = useNavigate();
  const location = useLocation();
  const stateProduct = (location.state as DemoLocationState | null)?.product;
  const initialProduct =
    stateProduct && normalizeDemoId(stateProduct.id).toLowerCase() === id.toLowerCase()
      ? stateProduct
      : null;
  const [product, setProduct] = useState<Product | null>(initialProduct);
  const [loading, setLoading] = useState(!initialProduct);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep list→detail navigation snappy when we already have the product.
      if (!initialProduct) {
        setLoading(true);
      }
      setError(null);

      const resolveFromShowcaseList = async (): Promise<Product | null> => {
        try {
          const list = await apiService.getProductShowcase();
          const needle = id.toLowerCase();
          return (
            list.find((p) => p.id === id) ??
            list.find((p) => p.id.toLowerCase() === needle) ??
            null
          );
        } catch {
          return null;
        }
      };

      try {
        const data = await apiService.getProductShowcaseById(id);
        if (!cancelled) {
          if (data) {
            setProduct(data);
            setError(null);
          } else {
            // Do not use router-state product; it may be stale vs current showcase.
            const fallback = await resolveFromShowcaseList();
            if (!cancelled) {
              if (fallback) {
                setProduct(fallback);
                setError(null);
              } else {
                setProduct(null);
                setError(t('public.home.demos.notFound'));
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const isNotFound = axios.isAxiosError(err) && err.response?.status === 404;
          // 404: consult showcase list only. Other errors: keep list→detail state.
          const fallback = isNotFound
            ? await resolveFromShowcaseList()
            : initialProduct;
          if (fallback) {
            setProduct(fallback);
            setError(null);
          } else {
            setProduct(null);
            setError(
              isNotFound ? t('public.home.demos.notFound') : t('public.home.demos.empty')
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t, initialProduct]);

  const images = useMemo(
    () => (product?.images || []).filter((u) => typeof u === 'string' && u.trim()),
    [product]
  );
  const descriptionText = stripHtml(product?.description || product?.shortDescription || '');
  const manufacturerUrl = product ? manufacturerSiteUrl(product) : undefined;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Skeleton width={180} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={320} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton height={40} width="60%" />
        <Skeleton height={24} width="40%" />
        <Skeleton height={120} sx={{ mt: 2 }} />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error || t('public.home.demos.notFound')}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/product-demos')}>
          {t('public.home.demos.backToList')}
        </Button>
      </Container>
    );
  }

  const pricing = product.pricing;
  const market = product.marketPricing;

  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Breadcrumbs sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/product-demos" underline="hover" color="inherit">
            {t('public.home.demos.title')}
          </MuiLink>
          <Typography color="text.primary">{product.name || product.model}</Typography>
        </Breadcrumbs>

        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/product-demos')}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          {t('public.home.demos.backToList')}
        </Button>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' },
            gap: 4,
            mb: 4,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...marketingPhotoPlateSx,
              minHeight: 320,
              cursor: images.length ? 'pointer' : 'default',
            }}
            onClick={() => images.length > 0 && setGalleryOpen(true)}
          >
            <Box
              component="img"
              src={productImage(product)}
              alt={product.name || product.model}
              sx={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = '/placeholder-camera.svg';
              }}
            />
          </Paper>

          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <Chip label={product.brand} color="primary" size="small" />
              {product.category ? <Chip label={product.category} size="small" variant="outlined" /> : null}
              {product.productType ? (
                <Chip label={product.productType} size="small" variant="outlined" />
              ) : null}
            </Stack>

            <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
              {product.name || product.model}
            </Typography>

            {(product.priceFormatted || product.price) ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" color="primary.main" fontWeight={700}>
                  {product.priceFormatted || formatMoney(product.price)}
                </Typography>
                {formatDateLabel(priceAsOfValue(product)) ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('public.home.demos.detail.priceAsOf')} {formatDateLabel(priceAsOfValue(product))}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {descriptionText ? (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                {descriptionText.length > 600 ? `${descriptionText.slice(0, 600)}…` : descriptionText}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {manufacturerUrl ? (
                <Button
                  variant="contained"
                  endIcon={<OpenInNew />}
                  href={manufacturerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('public.home.demos.detail.viewManufacturer')}
                </Button>
              ) : null}
              {images.length > 1 ? (
                <Button
                  variant="outlined"
                  startIcon={<PhotoLibrary />}
                  onClick={() => setGalleryOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('public.home.demos.detail.viewPhotos')} ({images.length})
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <ProductIdentifiersPanel product={product} />
          <WeightDimensionsPanel product={product} />

          {pricing && (pricing.msrp || pricing.retail || pricing.map || pricing.salePrice) ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                {t('public.home.demos.detail.pricing')}
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'MSRP', value: formatMoney(pricing.msrp) },
                  (() => {
                    // Show the after-instant-rebate advertised floor when a rebate window is active.
                    const em = getEffectiveMap(pricing);
                    if (em && em.windowStatus === 'active' && em.afterIR != null) {
                      return {
                        label: 'MAP (after IR)',
                        value: `${formatMoney(em.afterIR)} (MAP ${formatMoney(em.raw)})`,
                      };
                    }
                    return { label: 'MAP', value: formatMoney(pricing.map) };
                  })(),
                  { label: t('public.home.demos.detail.retail'), value: formatMoney(pricing.retail) },
                  { label: t('public.home.demos.detail.sale'), value: formatMoney(pricing.salePrice) },
                  {
                    label: t('public.home.demos.detail.priceAsOf'),
                    value: formatDateLabel(priceAsOfValue(product)),
                  },
                ]
                  .filter((r) => r.value)
                  .map((row) => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </Paper>
          ) : null}

          {market &&
          (market.msrp != null ||
            market.streetPriceLow != null ||
            market.streetPriceHigh != null ||
            market.streetPriceAvg != null ||
            market.dealerPrices?.length) ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                {t('public.home.demos.detail.marketPricing')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('public.home.demos.detail.marketPricingNote')}
              </Typography>
              <Stack spacing={1} sx={{ mb: market.dealerPrices?.length ? 1.5 : 0 }}>
                {[
                  { label: 'MSRP', value: formatMoney(market.msrp) },
                  { label: t('public.home.demos.detail.streetLow'), value: formatMoney(market.streetPriceLow) },
                  { label: t('public.home.demos.detail.streetHigh'), value: formatMoney(market.streetPriceHigh) },
                  { label: t('public.home.demos.detail.streetAvg'), value: formatMoney(market.streetPriceAvg) },
                ]
                  .filter((r) => r.value)
                  .map((row) => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
              {market.dealerPrices && market.dealerPrices.length > 0 ? (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1}>
                    {market.dealerPrices.map((dp) => (
                      <Box
                        key={`${dp.dealer}-${dp.price}`}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {dp.dealer}
                          {dp.inStock === false ? ` (${t('public.home.demos.detail.outOfStock')})` : ''}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatMoney(dp.price)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              ) : null}
            </Paper>
          ) : null}

          <ProductTimelinePanel product={product} />
        </Box>

        <FeaturesPanel product={product} />
        <InTheBoxPanel product={product} />
        <ResourcesPanel product={product} />

        {product.specifications && Object.keys(product.specifications).length > 0 ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {t('public.home.demos.detail.specifications')}
            </Typography>
            <SpecificationsViewer
              specifications={product.specifications}
              cameraModel={product.name || product.model}
              productType={product.productType}
            />
          </Paper>
        ) : null}

        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            ...marketingRaisedBandSx,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('public.home.demos.cta.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: marketingColor.textSecondary, mb: 2, maxWidth: 520, mx: 'auto' }}>
            {t('public.home.demos.cta.subtitle')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/signup')}
            sx={{
              ...marketingBandCtaSx,
              px: 3,
            }}
          >
            {t('public.home.demos.cta.button')}
          </Button>
        </Paper>

        {images.length > 0 ? (
          <ImageGallery
            images={images}
            title={product.name || product.model}
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
          />
        ) : null}
      </Container>
    </Box>
  );
}

export const ProductDemosPage: React.FC = () => {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = normalizeDemoId(rawId);
  const { t } = useTranslation() as { t: (key: string) => string };
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) return;
    let cancelled = false;
    (async () => {
      // Keep cached list visible when returning from detail; only skeleton on first load.
      if (products.length === 0) {
        setLoading(true);
      }
      try {
        const data = await apiService.getProductShowcase();
        if (!cancelled) {
          setProducts(data);
          setError(data.length === 0 ? t('public.home.demos.empty') : null);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
          setError(t('public.home.demos.empty'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  if (id) {
    return <ProductDetailView key={id} id={id} />;
  }

  return <ProductListView products={products} loading={loading} error={error} />;
};

export default ProductDemosPage;
